// Panel Pktmon — capture réseau temps réel via Windows Pktmon.
//
// Flow :
//   1. Operator clique "Démarrer" → tool pktmon_start_capture {filterIp?, filterPort?, filterProto?}
//      Retourne { captureId, jobId }
//   2. Le plugin C# stream chaque paquet via IJobController.Log() en JSON
//      → envelope live/plugin/job.event capté par ws.on()
//   3. Operator clique "Arrêter" → tool pktmon_stop_capture {captureId}
//   4. Au démontage du composant : auto-stop de toutes les captures actives
//      (cleanup pour ne pas laisser pktmon.exe orphelin).
//
// Affichage : table virtualisée (les 1000 dernières lignes pour ne pas
// exploser la mémoire UI), filtres rapides, stats live (count, bps).

import { createSignal, createMemo, onCleanup, onMount, For, Show, untrack, type Component } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { useToolClient } from "@/shared/tool-client";
import { useWs } from "@/shared/ws";
import { isWsEnvelope } from "@/shared/ws-protocol";
import { registerPanel } from "@/panel-registry";
import "./pktmon.css";

interface Packet {
  seq: number;
  at: string;
  direction: string;
  src: string;
  dst: string;
  proto: string;
  size: number;
  // Enrichissement côté agent (peut être absent si non résolu).
  srcDomain?: string | null;
  dstDomain?: string | null;
  service?: string | null;
  exe?: string | null;
}

interface CaptureStats {
  packets: number;
  bytes: number;
  uptimeSecs: number;
}

const PROTOS = ["", "TCP", "UDP", "ICMP"] as const;
const MAX_DISPLAY = 1000; // évite de saturer la RAM UI

// ─── Graphe de débit (sparkline SVG) ───────────────────────────────────────
const THROUGHPUT_WINDOW_MS = 1000; // taille d'un bucket temporel
const THROUGHPUT_BUCKETS = 60; // nombre de buckets affichés (≈ 60 s glissants)

type ThroughputMetric = "packets" | "bytes";

interface ThroughputBucket {
  packets: number;
  bytes: number;
}

// Agrège les paquets par fenêtre temporelle de THROUGHPUT_WINDOW_MS.
// Renvoie les THROUGHPUT_BUCKETS derniers buckets (anciens → récents).
function aggregateThroughput(packets: Packet[]): ThroughputBucket[] {
  if (packets.length === 0) return [];
  const byBucket = new Map<number, ThroughputBucket>();
  let minKey = Infinity;
  let maxKey = -Infinity;
  for (const p of packets) {
    const t = Date.parse(p.at);
    if (Number.isNaN(t)) continue;
    const key = Math.floor(t / THROUGHPUT_WINDOW_MS);
    if (key < minKey) minKey = key;
    if (key > maxKey) maxKey = key;
    const b = byBucket.get(key);
    if (b) {
      b.packets += 1;
      b.bytes += p.size || 0;
    } else {
      byBucket.set(key, { packets: 1, bytes: p.size || 0 });
    }
  }
  if (!Number.isFinite(minKey)) return [];
  // Comble les buckets vides pour une série temporelle continue.
  const startKey = Math.max(minKey, maxKey - THROUGHPUT_BUCKETS + 1);
  const series: ThroughputBucket[] = [];
  for (let k = startKey; k <= maxKey; k++) {
    series.push(byBucket.get(k) ?? { packets: 0, bytes: 0 });
  }
  return series;
}

// Mini-graphe en aire, autonome — pas de dépendance externe.
const ThroughputChart: Component<{ buckets: ThroughputBucket[]; metric: ThroughputMetric }> = (props) => {
  const W = 480;
  const H = 56;
  const PAD = 2;

  const values = () => props.buckets.map((b) => (props.metric === "bytes" ? b.bytes : b.packets));
  const peak = () => Math.max(1, ...values());

  const points = () => {
    const vals = values();
    const n = vals.length;
    if (n === 0) return [] as Array<{ x: number; y: number }>;
    const max = peak();
    const stepX = n > 1 ? (W - 2 * PAD) / (n - 1) : 0;
    return vals.map((v, i) => ({
      x: PAD + i * stepX,
      y: H - PAD - (v / max) * (H - 2 * PAD),
    }));
  };

  const linePath = () => {
    const pts = points();
    if (pts.length === 0) return "";
    return pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  };

  const areaPath = () => {
    const pts = points();
    if (pts.length === 0) return "";
    const first = pts[0];
    const last = pts[pts.length - 1];
    return `${linePath()} L${last.x.toFixed(1)},${(H - PAD).toFixed(1)} L${first.x.toFixed(1)},${(H - PAD).toFixed(
      1,
    )} Z`;
  };

  return (
    <svg
      class="pktmon-chart-svg"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Graphe de débit"
    >
      <Show
        when={props.buckets.length > 0}
        fallback={
          <text x={W / 2} y={H / 2 + 4} class="pktmon-chart-empty-text" text-anchor="middle">
            En attente de données…
          </text>
        }
      >
        <path class="pktmon-chart-area" d={areaPath()} />
        <path class="pktmon-chart-line" d={linePath()} />
      </Show>
    </svg>
  );
};

const PktmonPanel: Component = () => {
  const client = useToolClient();
  const ws = useWs();

  const [captureId, setCaptureId] = createSignal<string | null>(null);
  const [_jobId, setJobId] = createSignal<string | null>(null);
  const [packets, setPackets] = createSignal<Packet[]>([]);
  const [stats, setStats] = createSignal<CaptureStats>({ packets: 0, bytes: 0, uptimeSecs: 0 });
  const [status, setStatus] = createSignal<PanelStatus | undefined>();
  const [busy, setBusy] = createSignal(false);

  // Filtres (avant démarrage de capture)
  const [filterIp, setFilterIp] = createSignal("");
  const [filterPort, setFilterPort] = createSignal("");
  const [filterProto, setFilterProto] = createSignal<string>("");

  // Filtres post-capture (pour affichage uniquement)
  const [searchQuery, setSearchQuery] = createSignal("");
  const [autoScroll, setAutoScroll] = createSignal(true);

  // Métrique du graphe de débit (paquets/s ou octets/s par intervalle)
  const [chartMetric, setChartMetric] = createSignal<ThroughputMetric>("packets");

  let scrollRef: HTMLDivElement | undefined;
  let offWsListener: (() => void) | null = null;

  // Souscription aux events live/plugin/job.event filtrés par jobId courant.
  function subscribeJob(jid: string) {
    offWsListener?.();
    offWsListener = ws.on(
      (m) =>
        isWsEnvelope(m, "live", "plugin", "job.event") &&
        (m as { payload?: { pluginId?: string; jobId?: string } }).payload?.pluginId === "pktmon" &&
        (m as { payload?: { jobId?: string } }).payload?.jobId === jid,
      (m: unknown) => {
        const msg = m as { payload?: { event?: { type?: string; line?: string } } };
        const p = msg.payload?.event;
        if (!p || p.type !== "log" || !p.line) return;
        try {
          const obj = JSON.parse(p.line);
          if (obj.type === "packet") {
            setPackets((cur) => {
              const next = [...cur, obj as Packet];
              return next.length > MAX_DISPLAY ? next.slice(-MAX_DISPLAY) : next;
            });
            setStats((s) => ({
              packets: s.packets + 1,
              bytes: s.bytes + (obj.size || 0),
              uptimeSecs: s.uptimeSecs,
            }));
            if (untrack(autoScroll) && scrollRef) {
              requestAnimationFrame(() => scrollRef!.scrollTo(0, scrollRef!.scrollHeight));
            }
          } else if (obj.type === "stopped") {
            setStatus({ text: `Arrêté : ${obj.reason} (${obj.stats?.packets ?? 0} paquets)`, level: "info" });
            setCaptureId(null);
            setJobId(null);
          } else if (obj.type === "error") {
            setStatus({ text: `Erreur : ${obj.error}`, level: "danger" });
          }
        } catch {
          // Ignore les lignes non-JSON (rare avec notre format)
        }
      },
    );
  }

  async function startCapture() {
    if (busy() || captureId()) return;
    setBusy(true);
    setStatus({ text: "Démarrage capture…", level: "info" });
    setPackets([]);
    setStats({ packets: 0, bytes: 0, uptimeSecs: 0 });

    try {
      const params: Record<string, unknown> = {};
      if (filterIp().trim()) params.filterIp = filterIp().trim();
      if (filterPort().trim()) {
        const p = parseInt(filterPort().trim(), 10);
        if (!isNaN(p)) params.filterPort = p;
      }
      if (filterProto()) params.filterProto = filterProto();

      const r = (await client.call("pktmon_start_capture", params, { timeoutMs: 15_000 })) as {
        captureId: string;
        jobId: string;
      };
      if (!r.captureId || !r.jobId) throw new Error("Réponse invalide");
      setCaptureId(r.captureId);
      setJobId(r.jobId);
      subscribeJob(r.jobId);
      setStatus({ text: `Capture en cours (${r.captureId})`, level: "info" });
    } catch (e: unknown) {
      setStatus({ text: `Échec : ${e instanceof Error ? e.message : String(e)}`, level: "danger" });
    } finally {
      setBusy(false);
    }
  }

  async function stopCapture(silent = false) {
    const cid = captureId();
    if (!cid) return;
    if (!silent) setStatus({ text: "Arrêt en cours…", level: "info" });
    try {
      await client.call("pktmon_stop_capture", { captureId: cid }, { timeoutMs: 10_000 });
    } catch {
      // ignore — le plugin peut déjà avoir déclenché stop
    }
    setCaptureId(null);
    setJobId(null);
    offWsListener?.();
    offWsListener = null;
  }

  function clearPackets() {
    setPackets([]);
    setStats({ packets: 0, bytes: 0, uptimeSecs: 0 });
  }

  // Filtre d'affichage (post-capture) — recherche substring sur src/dst/proto
  const filteredPackets = createMemo(() => {
    const q = searchQuery().trim().toLowerCase();
    const list = packets();
    if (!q) return list;
    return list.filter(
      (p) =>
        p.src.toLowerCase().includes(q) ||
        p.dst.toLowerCase().includes(q) ||
        p.proto.toLowerCase().includes(q) ||
        (p.srcDomain ?? "").toLowerCase().includes(q) ||
        (p.dstDomain ?? "").toLowerCase().includes(q) ||
        (p.service ?? "").toLowerCase().includes(q) ||
        (p.exe ?? "").toLowerCase().includes(q),
    );
  });

  // Série temps-réel agrégée pour le graphe de débit.
  const throughputBuckets = createMemo(() => aggregateThroughput(packets()));
  const peakBucket = createMemo(() => {
    const b = throughputBuckets();
    if (b.length === 0) return 0;
    const m = chartMetric();
    return Math.max(...b.map((x) => (m === "bytes" ? x.bytes : x.packets)));
  });

  const totalKb = createMemo(() => (stats().bytes / 1024).toFixed(1));
  const pps = createMemo(() => {
    const u = stats().uptimeSecs || 1;
    return (stats().packets / u).toFixed(1);
  });

  // ─── Lifecycle ──────────────────────────────────────────────────────────
  // **CRUCIAL** : à chaque démontage du composant (changement de panel,
  // fermeture session, navigation), on STOP toute capture active. Ça évite
  // de laisser pktmon.exe tourner indéfiniment sur le host.
  onCleanup(() => {
    if (captureId()) void stopCapture(true);
    offWsListener?.();
  });

  // Tick uptime affichage (cosmétique uniquement)
  onMount(() => {
    const iv = setInterval(() => {
      if (captureId()) {
        setStats((s) => ({ ...s, uptimeSecs: s.uptimeSecs + 1 }));
      }
    }, 1000);
    onCleanup(() => clearInterval(iv));
  });

  const toolbar = (
    <>
      <Show
        when={!captureId()}
        fallback={
          <button class="btn btn--xs btn--danger" disabled={busy()} onClick={() => void stopCapture()}>
            ■ Arrêter
          </button>
        }
      >
        <button class="btn btn--xs btn--primary" disabled={busy()} onClick={() => void startCapture()}>
          ▶ Démarrer
        </button>
      </Show>
      <button class="btn btn--xs" onClick={clearPackets} title="Vider l'affichage">
        ✕ Effacer
      </button>
      <label class="pktmon-toggle">
        <input type="checkbox" checked={autoScroll()} onChange={(e) => setAutoScroll(e.currentTarget.checked)} />{" "}
        Auto-scroll
      </label>
    </>
  );

  return (
    <PanelLayout title="Trafic réseau (Pktmon)" toolbar={toolbar} status={status()}>
      <div class="pktmon-panel">
        {/* ─── Filtres capture (avant Start) ─── */}
        <div class="pktmon-filters">
          <label>
            <span>IP :</span>
            <input
              class="panel-filter-input"
              type="text"
              placeholder="192.168.1.42 (vide = toutes)"
              value={filterIp()}
              disabled={!!captureId()}
              onInput={(e) => setFilterIp(e.currentTarget.value)}
            />
          </label>
          <label>
            <span>Port :</span>
            <input
              class="panel-filter-input pktmon-port"
              type="text"
              placeholder="80"
              value={filterPort()}
              disabled={!!captureId()}
              onInput={(e) => setFilterPort(e.currentTarget.value)}
            />
          </label>
          <label>
            <span>Proto :</span>
            <select
              class="panel-filter-input pktmon-proto"
              value={filterProto()}
              disabled={!!captureId()}
              onChange={(e) => setFilterProto(e.currentTarget.value)}
            >
              <For each={PROTOS}>{(p) => <option value={p}>{p || "tous"}</option>}</For>
            </select>
          </label>
        </div>

        {/* ─── Stats live ─── */}
        <div class="pktmon-stats">
          <div class="pktmon-stat">
            <span class="pktmon-stat-label">Paquets</span>
            <span class="pktmon-stat-value">{stats().packets.toLocaleString()}</span>
          </div>
          <div class="pktmon-stat">
            <span class="pktmon-stat-label">Volume</span>
            <span class="pktmon-stat-value">{totalKb()} KB</span>
          </div>
          <div class="pktmon-stat">
            <span class="pktmon-stat-label">Débit</span>
            <span class="pktmon-stat-value">{pps()} pkt/s</span>
          </div>
          <div class="pktmon-stat">
            <span class="pktmon-stat-label">Durée</span>
            <span class="pktmon-stat-value">{stats().uptimeSecs}s</span>
          </div>
          <Show when={captureId()}>
            <div class="pktmon-stat">
              <span class="pktmon-stat-label">Statut</span>
              <span class="pktmon-stat-value pktmon-live">● LIVE</span>
            </div>
          </Show>
        </div>

        {/* ─── Graphe de débit (sparkline temps-réel) ─── */}
        <div class="pktmon-chart">
          <div class="pktmon-chart-header">
            <span class="pktmon-chart-title">Débit ({chartMetric() === "bytes" ? "octets/s" : "paquets/s"})</span>
            <span class="pktmon-chart-peak">
              pic : {chartMetric() === "bytes" ? `${(peakBucket() / 1024).toFixed(1)} KB` : peakBucket()}
            </span>
            <div class="pktmon-chart-toggle">
              <button
                type="button"
                class={`btn btn--xs${chartMetric() === "packets" ? " btn--primary" : ""}`}
                onClick={() => setChartMetric("packets")}
              >
                Paquets
              </button>
              <button
                type="button"
                class={`btn btn--xs${chartMetric() === "bytes" ? " btn--primary" : ""}`}
                onClick={() => setChartMetric("bytes")}
              >
                Octets
              </button>
            </div>
          </div>
          <ThroughputChart buckets={throughputBuckets()} metric={chartMetric()} />
        </div>

        {/* ─── Recherche post-capture ─── */}
        <div class="pktmon-search">
          <input
            class="panel-filter-input"
            type="text"
            placeholder="Filtrer (IP, port, proto) — recherche substring"
            value={searchQuery()}
            onInput={(e) => setSearchQuery(e.currentTarget.value)}
          />
          <span class="pktmon-search-count">
            {filteredPackets().length} / {packets().length}
          </span>
        </div>

        {/* ─── Table paquets ─── */}
        <div class="pktmon-table-wrap" ref={scrollRef}>
          <table class="pktmon-table">
            <thead>
              <tr>
                <th class="pktmon-col-seq">#</th>
                <th class="pktmon-col-time">Time</th>
                <th class="pktmon-col-dir">Dir</th>
                <th class="pktmon-col-src">Source</th>
                <th class="pktmon-col-dst">Destination</th>
                <th class="pktmon-col-proto">Proto</th>
                <th class="pktmon-col-svc">Service</th>
                <th class="pktmon-col-exe">Processus</th>
                <th class="pktmon-col-size">Size</th>
              </tr>
            </thead>
            <tbody>
              <Show
                when={filteredPackets().length > 0}
                fallback={
                  <tr>
                    <td colspan="9" class="pktmon-empty">
                      {captureId()
                        ? "En attente de paquets…"
                        : "Configurez les filtres et cliquez ▶ Démarrer pour lancer une capture."}
                    </td>
                  </tr>
                }
              >
                <For each={filteredPackets()}>
                  {(pkt) => (
                    <tr class={`pktmon-row pktmon-dir-${pkt.direction.toLowerCase()}`}>
                      <td class="pktmon-col-seq">{pkt.seq}</td>
                      <td class="pktmon-col-time">{pkt.at.slice(11, 19)}</td>
                      <td class="pktmon-col-dir">
                        <span class={`pktmon-badge pktmon-badge-${pkt.direction.toLowerCase()}`}>{pkt.direction}</span>
                      </td>
                      <td class="pktmon-col-src">
                        <span class="pktmon-addr">{pkt.src}</span>
                        <Show when={pkt.srcDomain}>
                          <span class="pktmon-domain" title={pkt.srcDomain!}>
                            {pkt.srcDomain}
                          </span>
                        </Show>
                      </td>
                      <td class="pktmon-col-dst">
                        <span class="pktmon-addr">{pkt.dst}</span>
                        <Show when={pkt.dstDomain}>
                          <span class="pktmon-domain" title={pkt.dstDomain!}>
                            {pkt.dstDomain}
                          </span>
                        </Show>
                      </td>
                      <td class="pktmon-col-proto">
                        <span class={`pktmon-proto pktmon-proto-${pkt.proto.toLowerCase()}`}>{pkt.proto}</span>
                      </td>
                      <td class="pktmon-col-svc">
                        <Show when={pkt.service} fallback={<span class="pktmon-muted">—</span>}>
                          <span class="pktmon-service">{pkt.service}</span>
                        </Show>
                      </td>
                      <td class="pktmon-col-exe">
                        <Show when={pkt.exe} fallback={<span class="pktmon-muted">—</span>}>
                          <span class="pktmon-exe" title={pkt.exe!}>
                            {pkt.exe}
                          </span>
                        </Show>
                      </td>
                      <td class="pktmon-col-size">{pkt.size}</td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </div>
      </div>
    </PanelLayout>
  );
};

registerPanel("pktmon", PktmonPanel);

export default PktmonPanel;
